import { Theme } from '@easeltv/client-sdk';
import { error } from '@easeltv/logger';
import {
    Component,
    createContext,
    ErrorInfo,
    PropsWithChildren,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';

import { GlobalStyle } from '../ThemeProvider/ThemeProvider.styled';
import { defaultErrorTheme } from './ErrorProvider.helpers';
import {
    ErrorBody,
    ErrorFooter,
    ErrorTitle,
    ErrorWrapper,
    LogoWrapper,
} from './ErrorProvider.styled';

/* ---------------------------------- Types --------------------------------- */

/**
 * FatalErrorI
 *
 * Shape of the object sent when using the `fatal` function
 */
export interface FatalErrorI {
    /** Text to show as the error title */
    title: string;

    /** Text to show as the error body */
    body: string;

    /** Text to show as the error footer */
    footer?: string;
}

/**
 * ErrorContextI
 *
 * Shape of the context object returned for use within the ErrorProvider
 */
export interface ErrorContextI {
    /** Function to call to throw a fatal error from within the application */
    fatal: (error: FatalErrorI) => void;

    /** Function to set the error theme, called if the real theme loads so errors can be branded */
    setErrorTheme: (theme: Theme) => void;
}

/* --------------------------------- Context -------------------------------- */

export const ErrorContext = createContext<ErrorContextI | undefined>(undefined);

/* ---------------------------------- Hook ---------------------------------- */

/**
 * useErrorReport
 *
 * Hook provides access to the ErrorContext from within the ErrorProvider
 *
 * @returns ErrorContext
 */
export const useErrorReport = () => {
    const context = useContext(ErrorContext);

    if (!context)
        throw new Error('Cannot use ErrorContext outside of ErrorProvider');

    return context;
};

/* ------------------------------ ErrorBoundary ----------------------------- */

class ErrorBoundary extends Component<
    PropsWithChildren,
    Record<string, never>
> {
    // eslint-disable-next-line react/static-property-placement
    declare context: ErrorContextI;

    public constructor(props: PropsWithChildren) {
        super(props);
    }

    public static getDerivedStateFromError(): Record<string, never> {
        // Update state so the next render will show the fallback UI.
        return {};
    }

    public componentDidCatch(err: Error, errorInfo: ErrorInfo) {
        const { fatal } = this.context;

        error('Uncaught error:', err, errorInfo);

        fatal({
            title: 'An error occurred',
            body: 'Please refresh and try again',
        });
    }

    public render() {
        const { children } = this.props;

        return children;
    }
}

ErrorBoundary.contextType = ErrorContext;

/* -------------------------------- Provider -------------------------------- */

/**
 * ErrorProvider
 *
 * @param { children } - The ReactNodes to be wrapped with this provider giving access to the ErrorContext
 * @returns
 */
const ErrorProvider = ({ children }: { children: ReactNode }) => {
    const [fatalError, setFatalError] = useState<FatalErrorI | undefined>();

    const isFatal = Boolean(fatalError);

    const [errorTheme, setErrorTheme] = useState<Theme>(defaultErrorTheme);

    const fatal = useCallback((err: FatalErrorI) => {
        setFatalError(err);
    }, []);

    const value = useMemo(() => ({ fatal, setErrorTheme }), [fatal]);

    return (
        <ErrorContext.Provider value={value}>
            {isFatal ? (
                <>
                    <GlobalStyle theme={errorTheme} />
                    <ErrorWrapper errorTheme={errorTheme}>
                        {errorTheme.sections.page.logo && (
                            <LogoWrapper
                                src={errorTheme.sections.page.logo}
                                alt="logo"
                            />
                        )}
                        <ErrorTitle errorTheme={errorTheme}>
                            {fatalError?.title}
                        </ErrorTitle>
                        <ErrorBody errorTheme={errorTheme}>
                            {fatalError?.body}
                        </ErrorBody>
                        {fatalError?.footer && (
                            <ErrorFooter errorTheme={errorTheme}>
                                {fatalError?.footer}
                            </ErrorFooter>
                        )}
                    </ErrorWrapper>
                </>
            ) : (
                <ErrorBoundary>{children}</ErrorBoundary>
            )}
        </ErrorContext.Provider>
    );
};

export default ErrorProvider;
